import { useBreakpointValue, Image, Box, chakra, shouldForwardProp } from '@chakra-ui/react'
import { isValidMotionProp, motion, useAnimation } from 'framer-motion'
import { ReactNode, useEffect } from 'react'
import { delay } from '@/helpers/delay'

export const GameItem = ({ item, isActive, compact = false }: { item: any; isActive: boolean; compact?: boolean }) => {
  const variant = useBreakpointValue(
    {
      base: '25px',
      md: compact ? '25px' : '50px',
    },
    {
      fallback: 'md',
    }
  )

  const borderRadiusVariant = useBreakpointValue(
    {
      base: '24px',
      md: compact ? '24px' : '40px',
      lg: compact ? '24px' : '40px',
    },
    {
      fallback: 'md',
    }
  )

  return (
    <motion.div
      variants={{
        active: {
          boxShadow: item.shadow.replace('${var}', variant),
        },
      }}
      initial={{
        boxShadow: `0px 0px ${variant} 0px rgba(255, 0, 0, 0) inset`,
      }}
      animate={isActive ? 'active' : ''}
      exit={{ boxShadow: `0px 0px ${variant} 0px rgba(255, 0, 0, 0) inset` }}
      style={{
        borderRadius: borderRadiusVariant,
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          transform: 'translateY(-50px)',
          scale: 0,
        }}
        animate={{
          opacity: 1,
          transform: 'translateY(0px)',
          scale: 1,
        }}
        exit={{ transform: 'translateY(50px)', opacity: 0 }}
        style={{
          borderRadius: borderRadiusVariant,
        }}
      >
        <Image src={item.src.src} alt={''} />
      </motion.div>
    </motion.div>
  )
}

export const BlurEffect = ({
  index,
  waiting,
  children,
  compact = false,
}: {
  index: number
  waiting: boolean
  children: ReactNode
  compact?: boolean
}) => {
  const controls = useAnimation()
  useEffect(() => {
    let isMounted = true

    async function runAnimationLoop() {
      while (isMounted) {
        if (!waiting) {
          return
        }

        await controls.start({
          left: ['-100%', '200%'],
          transition: {
            duration: 1,
            delay: index * 0.05,
            ease: 'linear',
          },
        })
        await delay(2000)
      }
    }

    runAnimationLoop()
    return () => {
      isMounted = false
    }
  }, [waiting])

  const borderRadiusVariant = useBreakpointValue(
    {
      base: '24px',
      md: compact ? '24px' : '40px',
      lg: compact ? '24px' : '40px',
    },
    {
      fallback: 'md',
    }
  )

  return (
    <Box position="relative" overflow="hidden" h="100%" w="100%" borderRadius={borderRadiusVariant}>
      <MotionBox
        position="absolute"
        top="0"
        left="-100%"
        width="50%"
        height="100%"
        bg="white"
        opacity={0.5}
        filter="blur(20px)"
        zIndex="0"
        animate={controls}
        transform="rotate(45deg)"
      />
      {children}
    </Box>
  )
}

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
})
